// src/services/backoffice/adminService.ts
// עם תיקון Token Expiry ושדה name וסיסמה
import { API_BASE_URL } from '../../config/api';
import { handleSessionError } from './authService'; // ✅ ייבוא הפונקציה החדשה

// const BASE_URL = 'http://localhost:3002';

// Types for Admin Management
export interface Admin {
  id: number;
  name: string; // ✅ הוספתי שדה name
  email: string;
  created_at: string;
  updated_at: string;
  is_deleted?: boolean;
}

export interface AdminDetails {
  id: number;
  name: string; // ✅ הוספתי שדה name
  email: string;
  created_at: string;
  updated_at: string;
  is_deleted?: boolean;
}

export interface GetAdminsParams {
  page?: number;
  limit?: number;
  search?: string;
  show_deleted?: boolean;
}

export interface GetAdminsResponse {
  admins: Admin[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

// ✅ תוקן - הוספתי שדה name
export interface SaveAdminData {
  id?: number; // 0 for new admin, >0 for existing (עשיתי אופציונלי)
  name: string; // ✅ שדה חובה!
  email: string;
  password?: string; // Required for new admin, optional for existing
  is_deleted?: boolean;
}

export interface ApiResponse<T = any> {
  res: 'OK' | 'NOK';
  msg: string;
  data?: T;
  error_code?: string;
}

// Helper function to get session token
const getSessionToken = (): string | null => {
  return localStorage.getItem('backoffice_session_id');
};

// Helper function to create FormData
const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  // ✅ הוספת session token (חובה!)
  const sessionToken = getSessionToken();
  if (sessionToken) {
    formData.append('session_token', sessionToken);
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });

  return formData;
};

// ✅ פונקציה חדשה לטיפול בresponse עם session error
const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  // ✅ בדיקת session expiry
  if (handleSessionError(result)) {
    // הפונקציה כבר טיפלה בשגיאה (ניקתה token והפנתה לlogin)
    throw new Error('Session expired - redirecting to login');
  }

  return result;
};

// Get list of admins with pagination and filters
export const getAdmins = async (params: GetAdminsParams = {}): Promise<any> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  const requestData = {
    page: params.page || 1,
    limit: params.limit || 25,
    search: params.search || '',
    show_deleted: params.show_deleted ? '1' : '0' // ✅ שינוי ל-string
  };

  const formData = createFormData(requestData);

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/get_admins`, {
    method: 'POST',
    body: formData
  });

  // ✅ שימוש בפונקציה החדשה
  const result: ApiResponse<GetAdminsResponse> = await handleApiResponse(response);

  if (result.res === 'NOK') {
    throw new Error(result.msg || 'Failed to fetch admins');
  }

  return result.data;
};

// Get admin details by ID
export const getAdminDetails = async (adminId: number): Promise<AdminDetails> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  const requestData = {
    admin_id: adminId
  };

  const formData = createFormData(requestData);

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/get_admin_details`, {
    method: 'POST',
    body: formData
  });

  // ✅ שימוש בפונקציה החדשה
  const result: ApiResponse<AdminDetails> = await handleApiResponse(response);

  if (result.res === 'NOK') {
    throw new Error(result.msg || 'Failed to fetch admin details');
  }

  return result.data!;
};

// ✅ Save admin - תוקן עם שדה name וסיסמה
export const saveAdmin = async (adminData: SaveAdminData): Promise<void> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  // ✅ ולידציה לשדה name
  if (!adminData.name?.trim()) {
    throw new Error('שם המנהל נדרש');
  }

  const requestData: Record<string, any> = {
    id: adminData.id || 0,
    name: adminData.name, // ✅ שדה חובה!
    email: adminData.email,
    is_deleted: adminData.is_deleted ? 1 : 0
  };

  // ✅ הוספת סיסמה רק אם יש ערך ולא ריק
  if (adminData.password && adminData.password.trim() !== '') {
    requestData.password = adminData.password;
  }

  // console.log('🛡️ [DEBUG] Saving admin with data:', requestData);

  const formData = createFormData(requestData);

  const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/save_admin`, {
    method: 'POST',
    body: formData
  });

  // ✅ שימוש בפונקציה החדשה
  const result: ApiResponse = await handleApiResponse(response);

  if (result.res === 'NOK') {
    console.error('🛡️ [DEBUG] Save admin failed:', result.msg);
    throw new Error(result.msg || 'Failed to save admin');
  }

  // console.log('🛡️ [DEBUG] Admin saved successfully');
};

// Toggle admin deleted status (soft delete)
export const toggleAdminDelete = async (adminId: number): Promise<void> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  // First get current admin details
  const adminDetails = await getAdminDetails(adminId);

  // Toggle the deleted status
  const newDeletedStatus = !adminDetails.is_deleted;

  // Save with new status
  await saveAdmin({
    id: adminId,
    name: adminDetails.name, // ✅ כולל שדה name
    email: adminDetails.email,
    is_deleted: newDeletedStatus
  });
};

// Restore deleted admin
export const restoreAdmin = async (adminId: number): Promise<void> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  // Get current admin details
  const adminDetails = await getAdminDetails(adminId);

  // Restore (set is_deleted to false)
  await saveAdmin({
    id: adminId,
    name: adminDetails.name, // ✅ כולל שדה name
    email: adminDetails.email,
    is_deleted: false
  });
};